import { ArrowRight } from 'lucide-react';
import { useMemo } from 'react';

import { SocialPostsGrid } from '../../posts/ui/social-post-card/social-posts-grid';
import { useFetchReferencedPosts } from '../hooks/use-fetch-referenced-posts';
import { useReferencedPostsNavigation } from '../hooks/use-referenced-posts-navigation';

import { RingSpinner } from '@/shared/components/loader/ring-spinner';
import { Button } from '@/shared/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import { Skeleton } from '@/shared/components/shadcn/skeleton';
import { ToggleStage } from '@/shared/components/stage/toggle-stage';
import type { SortOption } from '../../../../entities/post/api/index';

interface ReferencedPostsModalProps {
  isOpen: boolean;
  onClose: () => void;
  session_id: string;
  block_id: string;
  answer_id: string;
  parts: 'overview' | 'theme';
  part_id: string;
}

export function ReferencedPostsModal(props: ReferencedPostsModalProps) {
  const { session_id, block_id, answer_id, parts, part_id, isOpen, onClose } = props;
  const params = useMemo(
    () => ({
      limit: 3,
      order: 'score' as SortOption,
      session_id,
      block_id,
      answer_id,
      parts,
      part_id,
    }),
    [session_id, block_id, answer_id, parts, part_id],
  );

  const { data, isPending } = useFetchReferencedPosts(params, true);
  const toListPage = useReferencedPostsNavigation({
    session_id,
    block_id,
    answer_id,
    parts,
    part_id,
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent hidePrimitiveClose className="w-[1000px] max-w-[1000px]! gap-0 p-0">
        <DialogHeader className="border-b border-gray-200 p-6">
          <div className="flex justify-between gap-4">
            <ToggleStage
              status={!isPending}
              className="flex flex-col gap-2"
              fallback={
                <>
                  <DialogTitle>
                    <RingSpinner className="size-4 animate-spin" />
                  </DialogTitle>
                  <Skeleton className="h-8 w-24 rounded-md" />
                  <Skeleton className="h-4 w-60 rounded-md" />
                </>
              }
            >
              <DialogTitle>{data?.query_name}</DialogTitle>
              <DialogDescription>
                AI selected a few key posts that best support this answer. See more to explore
                further.
              </DialogDescription>
            </ToggleStage>

            <div className="flex items-center gap-2">
              <ToggleStage
                status={!isPending}
                fallback={<Skeleton className="h-8 w-24 rounded-md" />}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    onClose();
                    setTimeout(() => {
                      toListPage();
                    }, 100);
                  }}
                >
                  Show more
                  <ArrowRight className="size-4" />
                </Button>
              </ToggleStage>
            </div>
          </div>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
          <div className="space-y-4">
            <SocialPostsGrid
              className="pr-2"
              posts={data?.posts ?? []}
              isLoading={isPending}
              isFetchingNextPage={false}
              hasNextPage={false}
              onLoadMore={() => {}}
              skeletonCount={3}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
